import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAgentsApi } from '../apis/agents-apis';
import { Agent } from '../types';
import { Search, ChevronUp, Plus, Eye, Trash2, Loader2 } from 'lucide-react';
import _ from 'lodash';

import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Card, CardContent } from "../components/ui/Card";
import { Spinner } from "../components/ui/Spinner";
import { ConfirmationDialog } from '../components/ConfirmationDialog';
import { useToast } from "../components/ui/Toast";

export const AgentsList: React.FC = () => {
  const initialLoadRef = useRef(false);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [displayedAgents, setDisplayedAgents] = useState<Agent[]>([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<Agent[] | null>(null);
  
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [agentToDelete, setAgentToDelete] = useState<string | null>(null);
  const [isDeletingId, setIsDeletingId] = useState<string | null>(null);

  const agentsApi = useAgentsApi();

  const { addToast } = useToast();
  const navigate = useNavigate();
  const ITEMS_PER_PAGE = 20;

  const updateDisplayedAgents = useCallback((currentPage: number, agentsArray: Agent[]) => {
    const startIndex = 0;
    const endIndex = currentPage * ITEMS_PER_PAGE;
    setDisplayedAgents(agentsArray.slice(startIndex, endIndex));
    setPage(currentPage);
  }, []);

  const loadMoreAgents = useCallback(() => {
    if (!searchTerm && !loading) {
      const nextPage = page + 1;
      updateDisplayedAgents(nextPage, agents);
    }
  }, [searchTerm, loading, page, agents, updateDisplayedAgents]);

  const fetchAgents = useCallback(async () => {
    if (loading || initialLoadRef.current) return;
    
    setLoading(true);
    try {
      initialLoadRef.current = true;
      const getAllAgents = false;
      const fetchedAgents = await agentsApi.getAgents(getAllAgents);
      setAgents(fetchedAgents);
      setTotalCount(fetchedAgents.length);
      updateDisplayedAgents(1, fetchedAgents);
    } catch (error) {
      console.error('Error fetching agents:', error);
      addToast({
        title: "Error",
        description: "Failed to retrieve agents. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [loading, updateDisplayedAgents, addToast]);

  const handleSearch = _.debounce((term: string) => {
    setSearchTerm(term);
    if (term) {
      const results = agents.filter(agent =>
        agent.agentName.toLowerCase().includes(term.toLowerCase()) ||
        agent.agentDescription?.toLowerCase().includes(term.toLowerCase()) ||
        agent.agentTags?.some(tag => tag.toLowerCase().includes(term.toLowerCase()))
      );
      setSearchResults(results);
    } else {
      setSearchResults(null);
      updateDisplayedAgents(1, agents);
    }
  }, 300);

  const confirmDelete = (id: string) => {
    setAgentToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirmed = async () => {
    if (agentToDelete) {
      setIsDeletingId(agentToDelete);
      try {
        await agentsApi.deleteAgent(agentToDelete);
        addToast({
          title: "Success",
          description: "Agent successfully deleted",
          variant: "default",
        });
        initialLoadRef.current = false; // Reset the ref to allow a new fetch
        fetchAgents();
      } catch (error) {
        addToast({
          title: "Error",
          description: "Failed to delete agent. Please try again.",
          variant: "destructive",
        });
      } finally {
        setIsDeletingId(null);
        setDeleteDialogOpen(false);
        setAgentToDelete(null);
      }
    }
  };

  const handleView = (agent: Agent) => {
    navigate(`/marketplace/agents/edit/${agent.agentId}`);
  };

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  useEffect(() => {
    const handleScroll = _.throttle(() => {
      setShowScrollTop(window.scrollY > 400);

      if (!searchTerm && !loading) {
        const scrolledToBottom =
          window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 800;

        if (scrolledToBottom) {
          loadMoreAgents();
        }
      }
    }, 200);

    if (initialLoadRef.current) {
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, [searchTerm, loading, loadMoreAgents]);

  const currentAgents = searchResults || displayedAgents;

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-2xl mb-4">
        Agents Management
      </h1>

      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-2.5 text-muted-foreground" size={20} />
          <Input
            type="text"
            placeholder="Search agents..."
            className="w-full pl-10"
            onChange={(e) => handleSearch(e.target.value)}
          />
        </div>
      </div>

      <Button
        variant="default"
        className="mb-4 bg-green-500 hover:bg-green-600 text-white"
        onClick={() => navigate('/marketplace/agents/add')}
      >
        <Plus className="mr-2 h-4 w-4" />
        Add New Agent
      </Button>

      <div className="text-sm text-muted-foreground mb-4">
        {searchTerm ? (
          `Found ${searchResults?.length || 0} results for "${searchTerm}"`
        ) : (
          `Showing ${displayedAgents.length} of ${totalCount} agents`
        )}
      </div>

      <div className="grid gap-4">
        {currentAgents.map((agent) => (
          <Card key={agent.agentId} className="hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex justify-between items-start">
                <div className="flex-grow">
                  <h2 className="text-xl font-semibold">{agent.agentName}</h2>
                  <p className="text-muted-foreground mt-2">{agent.agentDescription}</p>
                  {agent.agentTags && agent.agentTags.length > 0 && (
                    <div className="relative">
                      <div className="flex flex-wrap gap-1 mt-2">
                        {agent.agentTags.slice(0, 3).map((tag, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center rounded-md bg-muted px-2 py-1 text-xs font-medium ring-1 ring-inset ring-muted-foreground/20"
                          >
                            {tag}
                          </span>
                        ))}
                        {agent.agentTags.length > 3 && (
                          <div className="relative inline-block group">
                            <span className="inline-flex items-center rounded-md bg-muted px-2 py-1 text-xs font-medium ring-1 ring-inset ring-muted-foreground/20 cursor-pointer">
                              +{agent.agentTags.length - 3}
                            </span>
                            <div className="absolute left-0 mt-2 p-2 bg-white border border-gray-200 rounded-md shadow-lg z-10 hidden group-hover:block">
                              {agent.agentTags.slice(3).map((tag, index) => (
                                <span
                                  key={index}
                                  className="block text-xs text-gray-700"
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex gap-2 ml-4">
                  <Button
                    variant="secondary"
                    onClick={() => handleView(agent)}
                    className="bg-blue-100 hover:bg-blue-200 text-blue-600"
                    disabled={isDeletingId === agent.agentId}
                  >
                    <Eye className="mr-2 h-4 w-4" />
                    View
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => confirmDelete(agent.agentId)}
                    className="bg-red-500 hover:bg-red-600 text-white"
                    disabled={isDeletingId === agent.agentId}
                  >
                    {isDeletingId === agent.agentId ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Deleting...
                      </>
                    ) : (
                      <>
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {loading ? (
        <div className="flex flex-col items-center my-8 gap-2">
          <Spinner className="border-blue-500" />
          <p className="text-center text-muted-foreground">Loading Agents...</p>
        </div>
      ) : currentAgents.length === 0 && (
        <Card className="p-8">
          <p className="text-center text-muted-foreground">
            {searchTerm ? 'No agents found matching your search.' : 'No agents found. Create a new agent to get started.'}
          </p>
        </Card>
      )}

      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDeleteConfirmed}
        title="Delete Agent"
        description="This will permanently delete the agent from the system. This action cannot be undone."
        confirmLabel={isDeletingId ? "Deleting..." : "Delete"}
        cancelLabel="Cancel"
        disabled={Boolean(isDeletingId)}
        requireVerification={true}
        verificationWord="DELETE"
      />

      {showScrollTop && (
        <Button
          variant="secondary"
          size="icon"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-8 right-8 rounded-full shadow-lg bg-blue-500 hover:bg-blue-600 text-white"
        >
          <ChevronUp size={24} />
        </Button>
      )}
    </div>
  );
};

export default AgentsList;